import { apiClient } from './api-client';
import { LoginResponse } from '../dto/auth/LoginResponse';
import { UserResponse } from '../dto/auth/UserResponse';
import { ErrorResponse } from '../dto/common/ErrorResponse';

/**
 * Authentication service.
 * Handles login, logout, and user profile operations.
 */
class AuthService {
  /**
   * Performs login request.
   * @param {LoginRequest} loginRequest - Login credentials
   * @returns {Promise<{success: boolean, data?: LoginResponse, error?: ErrorResponse}>}
   */
  async login(loginRequest) {
    try {
      // Validate request before sending
      const validation = loginRequest.validate();
      if (!validation.valid) {
        return {
          success: false,
          error: new ErrorResponse({
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Invalid input',
              details: validation.errors
            }
          })
        };
      }

      // Make API call
      const response = await apiClient.post('/auth/login', loginRequest.toJSON());

      // Validate response
      if (!response.data) {
        throw new Error('Empty response from server');
      }

      // Parse and validate login response
      const loginResponse = LoginResponse.fromJSON(response.data);
      if (!loginResponse.isValid()) {
        throw new Error('Invalid response format from server');
      }

      return {
        success: true,
        data: loginResponse
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Performs logout (clears local token).
   */
  async logout() {
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: new ErrorResponse({
          error: {
            code: 'LOGOUT_ERROR',
            message: 'Failed to logout properly'
          }
        })
      };
    }
  }

  /**
   * Gets current user profile from backend.
   * @returns {Promise<{success: boolean, data?: UserResponse, error?: ErrorResponse}>}
   */
  async getCurrentUser() {
    try {
      const response = await apiClient.get('/users/me');

      if (!response.data) {
        throw new Error('Empty response from server');
      }

      const userResponse = UserResponse.fromJSON(response.data);
      if (!userResponse.isValid()) {
        throw new Error('Invalid response format from server');
      }

      return {
        success: true,
        data: userResponse
      };

    } catch (error) {
      return {
        success: false,
        error: ErrorResponse.fromError(error)
      };
    }
  }

  /**
   * Checks if user is authenticated.
   */
  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return Boolean(token);
  }

  /**
   * Gets stored user data.
   */
  getUserData() {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  /**
   * Stores authentication data.
   */
  storeAuthData(loginResponse) {
    try {
      localStorage.setItem('authToken', loginResponse.token);
      localStorage.setItem('userData', JSON.stringify({
        userId: loginResponse.userId,
        name: loginResponse.name,
        email: loginResponse.email,
        tenantId: loginResponse.tenantId,
        roles: loginResponse.roles,
        onboardingPending: Boolean(loginResponse.onboardingPending),
      }));
      return true;
    } catch (error) {
      console.error('Error storing auth data:', error);
      return false;
    }
  }
}

export default new AuthService();
